/**
 * What a support attachment may be, and how one is read back — for BOTH surfaces.
 *
 * The console and the widget each had their own byte-identical copy of all of this.
 * That is the shape of duplication that has already cost this feature real defects: one
 * idea implemented twice drifts, and the halves disagree in a way nobody notices until
 * something is rendered on one screen that the other would have refused. Here the
 * allow-list is a security decision — an object URL is a document on THIS origin, so the
 * type the blob carries is what the browser executes — and a stale copy of it on one
 * surface is an XSS waiting to be found.
 *
 * The limits mirror helpers/supportAttachments.js and lib/uploader.js on the API, so a
 * file the server would refuse is named here rather than coming back as a failed send.
 * The server still enforces them; this is only so the sender finds out before the send.
 */
import { cqAPI } from "../../lib/Auth";

// How long an upload may take before it is called off. See uploadAttachment.
const UPLOAD_TIMEOUT_MS = 10 * 60 * 1000;

export const MAX_ATTACHMENTS = 5;
// Matches lib/uploader.js on the API, which hard-caps uploads at 10MB.
export const MAX_ATTACHMENT_MB = 10;
export const MAX_ATTACHMENT_BYTES = MAX_ATTACHMENT_MB * 1000 * 1000;

export const ALLOWED_ATTACHMENT_TYPES = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "application/pdf",
  "text/plain",
  "text/csv",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];
export const ATTACHMENT_ACCEPT = ALLOWED_ATTACHMENT_TYPES.join(",");

// Placeholder in the object-URL map for a file that is still downloading, so the loader
// does not start a second fetch for it.
export const BLOB_LOADING = "loading";

// An object URL is a document on THIS origin, so the type the blob carries is what the
// browser executes when the file is opened. That type can never come off the response:
// /file echoes back a `contenttype` it reads from the query string, and a customer can
// upload an HTML file. Only the type the API recorded — allow-listed to the set above,
// so never HTML — decides it.
const INLINE_SAFE_TYPES = new Set(ALLOWED_ATTACHMENT_TYPES);

// What a file whose recorded type is not on that list is saved as. Nothing renders it.
export const OPAQUE_TYPE = "application/octet-stream";

/** The allow-listed type the API stored for this file, or "" when it is not one. */
export const safeContentType = (attachment) => {
  const stored = String((attachment && attachment.content_type) || "").toLowerCase();
  return INLINE_SAFE_TYPES.has(stored) ? stored : "";
};

export const isImageAttachment = (attachment) => safeContentType(attachment).startsWith("image/");

/** Exactly the fields a message records about a file — never whatever else rode along. */
export const attachmentPayload = (attachment) => ({
  url: attachment.url,
  name: attachment.name,
  content_type: attachment.content_type,
  size: attachment.size,
});

export const formatBytes = (bytes) => {
  const size = Number(bytes) || 0;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * The idempotency token a send carries.
 *
 * Reused verbatim when a failed send is retried, so a message that COMMITTED but lost
 * its response is recognised by the server rather than stored twice.
 */
export const newClientMessageId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/**
 * Upload one file for a support message.
 *
 * A support attachment is never public: the API refuses any URL that did not come back
 * with `private=1`, and it must belong to the ticket's own practice. `bid` is what files
 * the object under `<bid>/<uuid>`; without it a PRIVATE upload lands at the bucket root,
 * where the server looks for it under the practice prefix and rejects the message as an
 * unuploaded file — and the object itself sits somewhere a plain id would serve to any
 * signed-in user of any practice. /upload reads the acl off the query string; the form
 * field carries the same intent for a server that reads it from the body instead.
 */
export const uploadAttachment = (file, bid) => {
  const form = new FormData();
  form.append("file", file, encodeURIComponent(file.name));
  form.append("acl", "private");
  const params = new URLSearchParams({ acl: "private" });
  if (bid) params.set("bid", String(bid));
  // Sent ONCE. The server names each private upload with a fresh uuid, so a retry does
  // not overwrite the previous attempt — it stores another copy. An upload that reached
  // S3 and lost its response would be repeated up to five times, leaving five copies of
  // somebody's screenshot in private storage with only the last one reachable from the
  // message. The person still has the file in front of them; asking them to try again is
  // the honest failure.
  //
  // Bounded, generously. A stalled upload used to hold the composer for good: the row
  // cannot be removed while it is in flight (an object landing afterwards would have
  // nothing referencing it) and Send refuses while any file is unfinished, so the only
  // way out was to abandon the draft. Ten minutes is far longer than ten megabytes takes
  // on any connection somebody is actually working over, and a deadline that passes ends
  // with the row gone and an error shown — which is a composer they can use again.
  return cqAPI.post(`/upload?${params.toString()}`, form, UPLOAD_TIMEOUT_MS, { attempts: 1 });
};

/**
 * Fetches a private file and hands back an object URL for it, typed `type` rather than
 * however the response described itself.
 */
// A bound on reading one attachment back. The support file endpoint streams from S3, so
// a response can begin and then stall — and an unbounded read leaves the thumbnail on its
// loading marker for good, with every further click starting another stalled request
// beside it. Generous: far longer than a 10 MB file takes on any connection somebody is
// really working over, and when it passes the callers' existing failure handling clears
// the marker and lets them try again.
export const ATTACHMENT_READ_TIMEOUT_MS = 2 * 60 * 1000;

export const attachmentBlobUrl = async (url, type) => {
  const downloaded = await cqAPI.downloadFile(url, { timeoutMs: ATTACHMENT_READ_TIMEOUT_MS });
  if (!downloaded) return null;
  try {
    const bytes = await (await fetch(downloaded)).blob();
    return window.URL.createObjectURL(bytes.slice(0, bytes.size, type));
  } finally {
    window.URL.revokeObjectURL(downloaded);
  }
};
